/**
 * Create Ticket Page
 * Creates new iTop tickets from Outlook emails
 */

import './taskpane.css';
import { CredentialService } from '../services/credential.service';
import { ITopApiService } from '../services/itop-api.service';
import { EmailService } from '../services/email.service';
import { ITopOrganization, ITopTeam, ITopService } from '../models/itop.models';
import {
  getElementById,
  showMessage,
  hideMessage,
  setButtonLoading,
  populateSelect,
  waitForOffice,
  goToSettings,
  escapeHtml
} from '../utils/helpers';

// Declare Quill as global
declare const Quill: {
  new (selector: string | HTMLElement, options?: Record<string, unknown>): QuillInstance;
};

interface QuillInstance {
  root: HTMLElement;
  getSemanticHTML(): string;
  setText(text: string): void;
  clipboard: {
    dangerouslyPasteHTML(html: string): void;
  };
}

// Editor instance
let descriptionEditor: QuillInstance | null = null;

// DOM Elements
let messageDiv: HTMLElement;
let loadingDiv: HTMLElement;
let formDiv: HTMLElement;
let senderDisplay: HTMLElement;
let organizationSelect: HTMLSelectElement;
let callerSelect: HTMLSelectElement;
let titleInput: HTMLInputElement;
let teamSelect: HTMLSelectElement;
let agentSelect: HTMLSelectElement;
let serviceSelect: HTMLSelectElement;
let createBtn: HTMLButtonElement;

// Data
let organizations: ITopOrganization[] = [];
let teams: ITopTeam[] = [];
let services: ITopService[] = [];

/**
 * Initialize the page
 */
async function initialize(): Promise<void> {
  await waitForOffice();

  // Check credentials
  if (!CredentialService.hasCredentials()) {
    showMessage(
      getElementById('message')!,
      'Please configure your iTop credentials first.',
      'warning'
    );
    setTimeout(goToSettings, 2000);
    return;
  }

  // Get DOM elements
  messageDiv = getElementById<HTMLElement>('message')!;
  loadingDiv = getElementById<HTMLElement>('loading')!;
  formDiv = getElementById<HTMLElement>('form')!;
  senderDisplay = getElementById<HTMLElement>('senderDisplay')!;
  organizationSelect = getElementById<HTMLSelectElement>('organization')!;
  callerSelect = getElementById<HTMLSelectElement>('caller')!;
  titleInput = getElementById<HTMLInputElement>('title')!;
  teamSelect = getElementById<HTMLSelectElement>('team')!;
  agentSelect = getElementById<HTMLSelectElement>('agent')!;
  serviceSelect = getElementById<HTMLSelectElement>('service')!;
  createBtn = getElementById<HTMLButtonElement>('createBtn')!;

  // Set up event listeners
  getElementById('settingsBtn')?.addEventListener('click', goToSettings);
  organizationSelect.addEventListener('change', handleOrganizationChange);
  teamSelect.addEventListener('change', handleTeamChange);
  createBtn.addEventListener('click', handleCreateTicket);

  // Load data
  await loadInitialData();
}

/**
 * Load initial data (organizations, teams, services, email)
 */
async function loadInitialData(): Promise<void> {
  loadingDiv.classList.add('show');
  formDiv.style.display = 'none';
  hideMessage(messageDiv);

  try {
    // Load dropdown data in parallel
    const [orgs, tms, svcs] = await Promise.all([
      ITopApiService.getOrganizations(),
      ITopApiService.getTeams(),
      ITopApiService.getServices()
    ]);

    organizations = orgs;
    teams = tms;
    services = svcs;

    // Populate dropdowns
    populateSelect(organizationSelect, organizations, 'id', 'name', 'Select organization...');
    populateSelect(teamSelect, [{ id: 0, name: 'None' }, ...teams], 'id', 'name');
    populateSelect(serviceSelect, [{ id: 0, name: 'None' }, ...services], 'id', 'name');

    // Load email data
    await loadEmailData();

    // Show form
    loadingDiv.classList.remove('show');
    formDiv.style.display = 'block';

    // Initialize TinyMCE
    await initializeEditor();

  } catch (error) {
    loadingDiv.classList.remove('show');
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Failed to load data: ${errorMessage}`, 'error');
  }
}

/**
 * Load email data and try to auto-match sender
 */
async function loadEmailData(): Promise<void> {
  if (!EmailService.isReady()) {
    senderDisplay.textContent = 'No email selected';
    return;
  }

  try {
    const emailData = await EmailService.getEmailData();

    // Display sender
    senderDisplay.textContent = emailData.senderEmail
      ? `${emailData.senderName} <${emailData.senderEmail}>`
      : 'Unknown sender';

    // Set title from subject
    titleInput.value = emailData.subject;

    // Store body for editor (will be set after TinyMCE init)
    (window as unknown as { emailBody: string }).emailBody = emailData.bodyType === 'html'
      ? EmailService.cleanHtmlBody(emailData.body)
      : EmailService.textToHtml(emailData.body);

    // Try to auto-match sender to iTop contact
    if (emailData.senderEmail) {
      await tryAutoMatchSender(emailData.senderEmail);
    }
  } catch (error) {
    console.warn('Failed to load email data:', error);
    senderDisplay.textContent = 'Failed to load email';
  }
}

/**
 * Try to auto-match sender email to iTop contact
 */
async function tryAutoMatchSender(email: string): Promise<void> {
  try {
    const person = await ITopApiService.findPersonByEmail(email);

    if (person) {
      // Select organization
      organizationSelect.value = String(person.org_id);

      // Load callers for this organization
      await handleOrganizationChange();

      // Select the matched caller
      callerSelect.value = String(person.id);

      showMessage(messageDiv, `Auto-matched sender to: ${person.friendlyname}`, 'success');
      setTimeout(() => hideMessage(messageDiv), 3000);
    }
  } catch (error) {
    console.warn('Auto-match failed:', error);
  }
}

/**
 * Initialize Quill editor
 */
function initializeEditor(): void {
  try {
    descriptionEditor = new Quill('#description', {
      theme: 'snow',
      modules: {
        toolbar: [
          ['bold', 'italic', 'underline'],
          [{ 'list': 'ordered' }, { 'list': 'bullet' }],
          ['link'],
          ['clean']
        ]
      },
      placeholder: 'Enter ticket description...'
    });

    // Set email body if available
    const emailBody = (window as unknown as { emailBody: string }).emailBody;
    if (emailBody && descriptionEditor) {
      descriptionEditor.clipboard.dangerouslyPasteHTML(emailBody);
    }
  } catch (error) {
    console.error('Failed to initialize Quill:', error);
  }
}

/**
 * Handle organization selection change
 */
async function handleOrganizationChange(): Promise<void> {
  const orgId = parseInt(organizationSelect.value);

  if (!orgId) {
    callerSelect.disabled = true;
    callerSelect.innerHTML = '<option value="" disabled selected>Select organization first...</option>';
    return;
  }

  callerSelect.disabled = true;
  callerSelect.innerHTML = '<option value="" disabled selected>Loading...</option>';

  try {
    const persons = await ITopApiService.getPersonsForOrganization(orgId);

    populateSelect(
      callerSelect,
      persons.map(p => ({ ...p, displayName: `${p.first_name} ${p.name}` })),
      'id',
      'displayName',
      'Select caller...'
    );

    callerSelect.disabled = false;
  } catch (error) {
    console.error('Failed to load callers:', error);
    callerSelect.innerHTML = '<option value="" disabled selected>Failed to load</option>';
  }
}

/**
 * Handle team selection change
 */
async function handleTeamChange(): Promise<void> {
  const teamId = parseInt(teamSelect.value);

  if (!teamId) {
    agentSelect.disabled = true;
    agentSelect.innerHTML = '<option value="">None</option>';
    return;
  }

  agentSelect.disabled = true;
  agentSelect.innerHTML = '<option value="" disabled selected>Loading...</option>';

  try {
    const agents = await ITopApiService.getAgentsForTeam(teamId);

    populateSelect(
      agentSelect,
      [{ id: 0, first_name: '', name: 'None', email: '', org_id: 0 }, ...agents].map(a => ({
        ...a,
        displayName: a.id ? `${a.first_name} ${a.name}` : 'None'
      })),
      'id',
      'displayName'
    );

    agentSelect.disabled = false;
  } catch (error) {
    console.error('Failed to load agents:', error);
    agentSelect.innerHTML = '<option value="">None</option>';
    agentSelect.disabled = false;
  }
}

/**
 * Handle create ticket button
 */
async function handleCreateTicket(): Promise<void> {
  // Validate required fields
  const orgId = parseInt(organizationSelect.value);
  const callerId = parseInt(callerSelect.value);
  const title = titleInput.value.trim();
  const description = descriptionEditor ? descriptionEditor.getSemanticHTML() : '';

  if (!orgId) {
    showMessage(messageDiv, 'Please select an organization.', 'error');
    return;
  }
  if (!callerId) {
    showMessage(messageDiv, 'Please select a caller.', 'error');
    return;
  }
  if (!title) {
    showMessage(messageDiv, 'Please enter a title.', 'error');
    return;
  }
  if (!description) {
    showMessage(messageDiv, 'Please enter a description.', 'error');
    return;
  }

  // Get optional fields
  const teamId = parseInt(teamSelect.value) || undefined;
  const agentId = parseInt(agentSelect.value) || undefined;
  const serviceId = parseInt(serviceSelect.value) || undefined;
  const priority = document.querySelector<HTMLInputElement>('input[name="priority"]:checked')?.value || 'medium';

  setButtonLoading(createBtn, true, 'Creating...');
  hideMessage(messageDiv);

  try {
    const result = await ITopApiService.createTicket({
      org_id: orgId,
      caller_id: callerId,
      title,
      description,
      team_id: teamId,
      agent_id: agentId,
      service_id: serviceId,
      priority
    });

    const baseUrl = CredentialService.getUrl().replace(/\/webservices\/rest\.php.*$/, '');
    const ticketUrl = `${baseUrl}/pages/UI.php?operation=details&class=UserRequest&id=${result.id}`;
    showMessage(
      messageDiv,
      `Ticket created successfully! Reference: <a href="${ticketUrl}" target="_blank">${escapeHtml(result.ref)}</a>`,
      'success',
      true
    );

    // Clear form for next ticket
    titleInput.value = '';
    if (descriptionEditor) {
      descriptionEditor.setText('');
    }

  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    showMessage(messageDiv, `Failed to create ticket: ${errorMessage}`, 'error');
  } finally {
    setButtonLoading(createBtn, false);
  }
}

// Register for email item changes
EmailService.onItemChanged(() => {
  loadEmailData();
});

// Initialize when Office is ready
Office.onReady(() => {
  initialize();
});
